import { useCallback } from 'react'
import { IntegrationServiceId } from '../../../../common/integrations'
import { NoticeKey } from '../../../../models/Notice'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'
import { useGetNoticesQuery, useUpdateNoticeMutation } from '../../../../store/notices/notices'
import { useCalendarCapability } from '../../../../store/onboarding/useCalendarCapability'
import { useMailCapability } from '../../../../store/onboarding/useMailCapability'
import { useZoomCapability } from '../../../../store/onboarding/useZoomCapability'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'

export type UpcomingEmptyStateKey = 'primary' | 'zoom' | 'email'

export interface UseUpcomingPageOnboardingBannersResult {
  activeState: UpcomingEmptyStateKey | null
  isFetching: boolean
  connectedWithoutSync: IntegrationServiceId[]
  allowedCalendarIds: IntegrationServiceId[]
  allowedCalendarSecondaryIds: IntegrationServiceId[]
  allowedMailIds: IntegrationServiceId[]
  dismissCalendar: () => void
  dismissZoom: () => void
  dismissEmail: () => void
  isDismissingCalendar: boolean
  isDismissingZoom: boolean
  isDismissingEmail: boolean
}

export const useUpcomingPageOnboardingBanners = (): UseUpcomingPageOnboardingBannersResult => {
  const accountId = useAppSelector(selectAccountId)

  const calendarCapability = useCalendarCapability()
  const zoomCapability = useZoomCapability()
  const mailCapability = useMailCapability()
  const { hasQuota, isFetching: isQuotaFetching } = useZocksQuota()

  const isCapabilitiesLoading = calendarCapability.isLoading || zoomCapability.isLoading || mailCapability.isLoading

  const { data: noticesData, isFetching: isNoticesFetching } = useGetNoticesQuery({ accountId }, { skip: isCapabilitiesLoading })
  const [updateCalendarNotice, { isLoading: isDismissingCalendar }] = useUpdateNoticeMutation()
  const [updateZoomNotice, { isLoading: isDismissingZoom }] = useUpdateNoticeMutation()
  const [updateEmailNotice, { isLoading: isDismissingEmail }] = useUpdateNoticeMutation()

  const isCalendarDismissed = noticesData?.data?.find((n) => n.key === NoticeKey.UPCOMING_EMPTY_STATE)?.isDismissed ?? false
  const isZoomDismissed = noticesData?.data?.find((n) => n.key === NoticeKey.UPCOMING_ZOOM_ONBOARDING_BANNER)?.isDismissed ?? false
  const isEmailDismissed = noticesData?.data?.find((n) => n.key === NoticeKey.UPCOMING_EMAIL_ONBOARDING_BANNER)?.isDismissed ?? false

  const isDismissing = isDismissingCalendar || isDismissingZoom || isDismissingEmail

  const emailEnabled = hasQuota('integration.emailEnabled.value')

  const isFetching = isCapabilitiesLoading || isNoticesFetching || isQuotaFetching || isDismissing

  const activeState: UpcomingEmptyStateKey | null = isFetching
    ? null
    : calendarCapability.isAllowed && !calendarCapability.isFulfilled && !isCalendarDismissed && !isDismissingCalendar
      ? 'primary'
      : zoomCapability.isAllowed && !zoomCapability.isFulfilled && !isZoomDismissed && !isDismissingZoom
        ? 'zoom'
        : mailCapability.isAllowed && !mailCapability.isFulfilled && !isEmailDismissed && !isDismissingEmail && !emailEnabled
          ? 'email'
          : null

  const dismissCalendar = useCallback(() => {
    updateCalendarNotice({ accountId, key: NoticeKey.UPCOMING_EMPTY_STATE, isDismissed: true })
  }, [accountId, updateCalendarNotice])

  const dismissZoom = useCallback(() => {
    updateZoomNotice({ accountId, key: NoticeKey.UPCOMING_ZOOM_ONBOARDING_BANNER, isDismissed: true })
  }, [accountId, updateZoomNotice])

  const dismissEmail = useCallback(() => {
    updateEmailNotice({ accountId, key: NoticeKey.UPCOMING_EMAIL_ONBOARDING_BANNER, isDismissed: true })
  }, [accountId, updateEmailNotice])

  return {
    activeState,
    isFetching,
    connectedWithoutSync: calendarCapability.connectedWithoutSync,
    allowedCalendarIds: calendarCapability.allowedServiceIds,
    allowedCalendarSecondaryIds: calendarCapability.allowedSecondaryIds ?? [],
    allowedMailIds: mailCapability.allowedServiceIds,
    dismissCalendar,
    dismissZoom,
    dismissEmail,
    isDismissingCalendar,
    isDismissingZoom,
    isDismissingEmail,
  }
}
